<template>
  <div class="user">
    <div v-if="!selectedCustomField" class="no user-card">
      <span>{{$t('SelectCustomField')}}</span>
    </div>
    <div v-if="!!selectedCustomField" class="user-info-container">
      <object-main-info :fields="fields" :identifier="customField ? customField.label : ''" :identifier-label="$tc('Label',1)"
        :editing-data="editingData" :show-edit-button="actionToExecute !== 'addCustomField'" :card-label="cardLabel"
        :height="'100%'" :submit-button-label="submitButtonLabel" :required-fields-filled="requiredFieldsAreFilled"
        showEditActionButtons @toggle-edit="toggleEdit"
        @update-object="updateCustomField" @save-object="saveObject" @cancel-edit="discardChangesDialogIsOpen = true">
      </object-main-info>
    </div>
    <loading-spinner :active.sync="loading" :is-full-page="false" color="#29BDF0" style="position: absolute;" />
    <dialog-choice :isOpen="saveChangesDialogIsOpen" :title="$t('SaveChanges')" :content="$t('SaveChangesContent')"
      :mainActionLabel="$t('Confirm')" @mainAction="saveObject" :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="saveChangesDialogIsOpen = false" icon="mdi-content-save" iconColor="#29BDF0" />
    <dialog-choice :isOpen="discardChangesDialogIsOpen" :title="$t('DiscardChanges')"
      :content="$t('ClusterCreationConfirmation')" :mainActionLabel="$t('Confirm')" @mainAction="cancelEdit"
      :secondaryActionLabel="$t('Cancel')" @secondaryAction="discardChangesDialogIsOpen = false" icon="mdi-delete"
      iconColor="#29BDF0" />
    <dialog-choice :isOpen="tokenChangesDialogIsOpen" :title="$t('RevokeToken')"
      :content="$t('ClusterCreationConfirmation')" :mainActionLabel="$t('Confirm')" @mainAction="revokeToken"
      :secondaryActionLabel="$t('Cancel')" @secondaryAction="cancelRevokeToken" icon="mdi-lock" iconColor="#29BDF0" />
  </div>
</template>

<script>

import { mapActions } from 'vuex';
import { bus } from "@/main.js";
import _ from 'lodash';
import utils from "@/utils";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import { objectManagementMixin } from "@/mixins/objectManagementMixin";
import { createEditPageMixin } from "@/mixins/management/createEditPageMixin";
import { listsMixin } from "@/mixins/listsMixin";
import { customFieldsMixin } from "@/mixins/customFieldsMixin";
import { materialFamilyMixin } from "@/mixins/management/materialFamilyMixin";
import LoadingSpinner from "vue-loading-overlay";
import ObjectMainInfo from '@/newComponents/_Organisms/ObjectMainInfo';
import DialogChoice from '@/newComponents/_Molecules/DialogChoice.vue';
import CustomButton from '@/newComponents/_Atoms/CustomButton'

export default {
  name: "CustomFields",

  components: {
    LoadingSpinner,
    DialogChoice,
    ObjectMainInfo,
    CustomButton,
  },

  mixins: [checkPermissionsMixin, objectManagementMixin, createEditPageMixin, customFieldsMixin, listsMixin, materialFamilyMixin],

  props: {
    selectedCustomField: {
      type: Object,
    },
  },

  data() {
    return {
      objectValue: null,
      loading: false,
      customField: {},
      editingData: false,
      originalCustomField: {},
      specificRequiredFieldsAreFilled: false,
      dataToSend: {},
      actionToExecute: 'UPDATE_SELECTED_CustomField',
      submitButtonLabel: 'Save',
      cardLabel: 'EditCustomField',
      options:[],
      list_name:'list_user_type',
      saveChangesDialogIsOpen: false,
      discardChangesDialogIsOpen: false,
      tokenChangesDialogIsOpen: false,
      passwordIsValid: false,
      buttonStyling: {
        'padding': '0px',
        'drop-shadow': '0.85px 0.85px 1.75px rgba(0, 0, 0, 0.25)',
        'border-radius': '4px',
      },

      topCardHeight: {
        style: {
          maxHeight: '50%',
          height: 'auto',
        },
      },
    }
  },

  mounted() {
    this.getCustomLists(this.customField);
  },

  destroyed() {
  },

  watch: {
    selectedCustomField(value) {
      this.getCustomLists(value);
      if (value !== null && Object.keys(value).length === 0) { //if user is an empty object for creation
        this.setCreateCustomFieldVariables();
      } else if (!!value) { // if the user is a normal object to be edited
        this.setEditCustomFieldVariables(value.uuid);
      } else if (value === null) { // if there is no object
        this.clearVariables();
      }
    },

    'customField.object_type'(value) {
      if (this.forms.find(form => form.id === value)) {
        this.customField.table = value;
      } else if (
        this.objectsAndMaterialFamilies.find(
          family => family.id === value
        )
      ) {
        this.customField.object_type = value;
        this.customField.table = "fc_stock_management_materials";
      } else if (!value) {
        this.customField.object_type = null;
        this.customField.table = null;
      }
    },

    'customField.input_type': function(newListName, oldListName) {
      if (newListName !== oldListName) {
        this.customField.list_name = null
        this.customField.default_value = null
      }
    },

    'customField.list_name': function(newListName, oldListName) {
      if (newListName !== oldListName) {
        this.customField.default_value = null
      }
    },

    editingData() {
      bus.$emit("toggle-create-edit-flag")
    },
  },

  computed: {
    customFieldObjectOptions() {
      return [
        {
          id: "families",
          label: this.$i18n.tc("Family", 2),
          children: this.objectsAndMaterialFamilies
        },
        {
          id: "forms",
          label: this.$i18n.tc("Form", 2),
          children: this.forms
        }
      ];
    },

    requiredFieldsAreFilled() {
      return this.customField &&
      !!this.customField.label &&
      !!this.customField.code &&
      !!this.customField.table &&
      !!this.customField.input_type &&
      !!this.customField.size
    },

    creatingCustomField() {
      return this.actionToExecute === 'addCustomField';
    },

    fields() {
      let fields = [];
      if (this.customField !== null) {
        fields = [
          {
            icon: "code",
            label: this.$i18n.tc("Code", 1),
            value: this.customField.code,
            size: '100%',
            varName: 'code',
            inputType: 'text',
            disabled: false,
            required: true,
          },
          {
            icon: "label",
            label: this.$i18n.tc("Object", 1),
            value: this.customField.object_type,
            options: this.customFieldObjectOptions,
            size: '100%',
            varName: 'object_type',
            inputType: 'select',
            disabled: false,
            required: true,
            disableBranchNodes: true,
          },
          {
            icon: "layers",
            label: this.$i18n.tc("Type", 1),
            value: this.customField.input_type,
            options: this.fieldTypes,
            size: '100%',
            varName: 'input_type',
            inputType: 'select',
            disabled: false,
            required: true,
          },
          {
            icon: "list",
            label: this.$i18n.tc("List"),
            value: this.customField.list_name,
            options: this.customLists,
            size: '100%',
            varName: 'list_name',
            inputType: 'select',
            disabled: !(this.customField.input_type == "select"),
            required: false,
          },
          {
            icon: "settings_backup_restore",
            label: this.$i18n.tc("Default Value"),
            value: this.customField.default_value,
            size: '100%',
            varName: 'default_value',
            inputType: 'text',
            disabled: (this.customField.list_name == null || this.customField.list_name == undefined),
            required: false,
          },
          {
            icon: "straighten",
            label: this.$i18n.tc("Size", 1),
            value: this.customField.size,
            options: this.customFieldSizeOptions,
            size: '100%',
            varName: 'size',
            inputType: 'select',
            disabled: false,
            required: true,
          },
          {
            icon: "emergency",
            label: this.$i18n.tc("Required"),
            value: this.editingData
              ? this.customField.required ? 'True' : 'False'
              : this.customField.required ? this.$i18n.t('Yes') : this.$i18n.t('No'),
            size: '100%',
            required: false,
            inputType: 'radio',
            radioOptions: [{ value: 'True', label: this.$i18n.t('Yes') }, { value: 'False', label: this.$i18n.t('No') }],
            varName: 'required',
            size: this.editingData ? '50%' : '100%',
          },
          {
            icon: "toggle_off",
            label: this.$i18n.tc("Disabled"),
            value: this.editingData
              ? this.customField.disable ? 'True' : 'False'
              : this.customField.disable ? this.$i18n.t('Yes') : this.$i18n.t('No'),
            size: '100%',
            required: false,
            inputType: 'radio',
            radioOptions: [{ value: 'True', label: this.$i18n.t('Yes') }, { value: 'False', label: this.$i18n.t('No') }],
            varName: 'disable',
            size: this.editingData ? '50%' : '100%',
          },
          {
            icon: "format_list_numbered",
            label: this.$i18n.tc("Index"),
            value: this.customField.index,
            size: '100%',
            varName: 'index',
            inputType: 'int_pos',
            disabled: false,
            required: false,
          },
        ];
        if (this.editingData) {
          fields.splice(0, 0, {
            label: this.$i18n.tc("Label"),
            value: this.customField.label,
            disabled: false,
            required: true,
            inputType: 'text',
            varName: 'label',
            size: '100%',
          },
          )
        }
      }
      return fields;
    },

  },

  methods: {
    ...mapActions([
      "fetchCustomFieldByUuid",
      "GET_LIST",
      "editCustomField",
      "createCustomField",
    ]),


    async getCustomFieldByUuid(uuid) {
      this.loading = true;
      try {
        this.customField = await this.fetchCustomFieldByUuid(uuid);

        this.originalCustomField = _.cloneDeep(this.customField);

      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async saveObject() {
      try {
        this.dataToSend = _.cloneDeep(this.customField);
        if (this.actionToExecute === "addCustomField") {
          if(this.dataToSend.table !== 'fc_stock_management_materials'){
            this.dataToSend.object_type = null;
          }
          await this.createCustomField(this.dataToSend);
          this.$toasted.global.toast_success(this.$i18n.t("CustomFieldCreatedSuccessfully"));
        } else {
          await this.editCustomField(this.dataToSend);
          this.$toasted.global.toast_success(this.$i18n.t("CustomFieldEditedSuccessfully"));
        }
        this.editingData = false;
        bus.$emit('reload-custom-fields');
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loading = false;
        this.dataToSend = {};
      }
    },

    updateCustomField(updatedValue) {
      this.customField[updatedValue.varName] = updatedValue.value;
    },

    cancelEdit() {
      this.discardChangesDialogIsOpen = false;
      this.editingData = false;
      if (this.customField.id) {
        this.customField = _.cloneDeep(this.originalCustomField);
      } else {
        this.$emit('clear-selected-item');
      }
    },

    toggleEdit() {
      if (this.editingData) {
        this.discardChangesDialogIsOpen = true;
      } else {
        this.editingData = !this.editingData
      }
    },

    cancelRevokeToken() {
      this.tokenChangesDialogIsOpen = false;
    },

    toggleLock() {
      this.tokenChangesDialogIsOpen = true;
    },


    /** Fills the object to send to the server by putting a watcher in each variable of the object */
    createDataToSend() {

      Object.keys(this.customField).forEach(key => {
        if (this.customField[key] !== this.originalCustomField[key]) {
          this.dataToSend[key] = this.customField[key];
        }
      });
    },

    handleError(error) {
      let errorHandle = utils.errorHandler(error);

      if (typeof errorHandle === "object") {
        if (errorHandle.hasOwnProperty("email")) {
          this.$toasted.global.toast_success(this.$i18n.t('ValidEmail'));
        }
        if (errorHandle.hasOwnProperty("password")) {
          this.$toasted.global.toast_success(this.$i18n.t('ValidPassword'));
        }
        if (errorHandle.hasOwnProperty("name")) {
          this.$toasted.global.toast_success(this.$i18n.t('ValidName'));
        }
      } else if (typeof errorHandle === "string") {
        this.$toasted.global.toast_error(errorHandle);
      }
    },

    setCreateCustomFieldVariables() {
      this.customField = {
        code: undefined,
        created_at: null,
        default_value: null,
        deleted_at: null,
        disable: false,
        id: undefined,
        index: null,
        input_type: undefined,
        label: undefined,
        list_name: null,
        object_type: null,
        required: false,
        size: undefined,
        table: undefined,
        updated_at: undefined,
        uuid: undefined,
      };
      this.originalCustomField = {},
      this.actionToExecute = 'addCustomField';
      this.submitButtonLabel = 'Create';
      this.cardLabel = 'AddCustomField';
      this.editingData = true;
    },

    setEditCustomFieldVariables(uuid) {
      this.getCustomFieldByUuid(uuid);
      this.actionToExecute = 'editCustomFieldByUuid';
      this.submitButtonLabel = 'Save';
      this.cardLabel = 'EditCustomField';
    },

    clearVariables() {
      this.customField = null;
      this.originalCustomField = null;
      this.editingData = false;
    },
  },

}
</script>

<style scoped>
.user {
  height: 100%;
  max-height: 100%;
  background-color: #fff;
  position: relative;
}

.user-card {
  height: 100%;
}

.user-card {
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
}

.no.user-card {
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
}

.no.user-card>span {
  color: #29BDF0;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
}

.user-info-container {
  gap: 8px;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  height: 100%;
}

.user-logo {
  background-color: white;
  border-radius: 8px;
  height: 40px;
  width: 40px;
  box-shadow: 1.75px 1.75px 4.75px 0px rgba(0, 0, 0, 0.25);
  margin-right: 16px;
  margin-left: 2px;
}

.edit-image-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 10px 0;
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/newComponents/SystemSettingsManagement/CustomFields/CustomFields.vue